import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, Award, Target, Zap, Calendar, Search, ChevronLeft, ChevronRight, Clock, Trophy, Moon, Sun } from 'lucide-react';

const CodeforcesDashboard = () => {
  const [handle, setHandle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState('recent');
  const [activeTab, setActiveTab] = useState('problems');
  const [darkMode, setDarkMode] = useState(false);
  
  const itemsPerPage = 15;

  // Load theme preference on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
    }
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    localStorage.setItem('theme', !darkMode ? 'dark' : 'light');
  };

  const fetchUserData = async () => {
    if (!handle.trim()) {
      setError('Please enter a handle');
      return;
    }

    setLoading(true);
    setError('');
    setData(null);

    try {
      const userInfoRes = await fetch(`https://codeforces.com/api/user.info?handles=${handle}`);
      const userInfoData = await userInfoRes.json();
      
      if (userInfoData.status !== 'OK') {
        throw new Error('User not found');
      }

      const submissionsRes = await fetch(`https://codeforces.com/api/user.status?handle=${handle}`);
      const submissionsData = await submissionsRes.json();
      
      if (submissionsData.status !== 'OK') {
        throw new Error('Failed to fetch submissions');
      }

      const ratingRes = await fetch(`https://codeforces.com/api/user.rating?handle=${handle}`);
      const ratingData = await ratingRes.json();

      const processedData = processUserData(
        userInfoData.result[0],
        submissionsData.result,
        ratingData.status === 'OK' ? ratingData.result : []
      );

      setData(processedData);
      setCurrentPage(0);
    } catch (err) {
      setError(err.message || 'Failed to fetch data. Please check the handle and try again.');
    } finally {
      setLoading(false);
    }
  };

  const processUserData = (userInfo, submissions, ratingHistory) => {
    const solvedMap = new Map();
    let acceptedCount = 0;

    submissions.forEach(sub => {
      if (sub.verdict === 'OK') {
        acceptedCount++;
        if (sub.problem) {
          const key = `${sub.problem.contestId}-${sub.problem.index}`;
          if (!solvedMap.has(key)) {
            solvedMap.set(key, {
              name: sub.problem.name,
              rating: sub.problem.rating || 0,
              index: sub.problem.index,
              tags: sub.problem.tags || [],
              contestId: sub.problem.contestId,
              timestamp: sub.creationTimeSeconds
            });
          }
        }
      }
    });

    const solvedProblems = Array.from(solvedMap.values());
    const { currentStreak, longestStreak } = calculateStreaks(submissions);

    const ratingDist = {};
    solvedProblems.forEach(p => {
      if (p.rating > 0) {
        const bucket = Math.floor(p.rating / 100) * 100;
        ratingDist[bucket] = (ratingDist[bucket] || 0) + 1;
      }
    });

    const ratingDistData = Object.entries(ratingDist)
      .map(([rating, count]) => ({ rating: parseInt(rating), count }))
      .sort((a, b) => a.rating - b.rating);

    const diffDist = {};
    solvedProblems.forEach(p => {
      const diff = p.index[0] || 'Unknown';
      diffDist[diff] = (diffDist[diff] || 0) + 1;
    });

    const diffDistData = Object.entries(diffDist)
      .map(([difficulty, count]) => ({ difficulty, count }))
      .sort((a, b) => a.difficulty.localeCompare(b.difficulty));

    const ratingProgressData = ratingHistory.map(r => ({
      date: new Date(r.ratingUpdateTimeSeconds * 1000).toLocaleDateString(),
      rating: r.newRating
    }));

    // Process contests
    const contests = ratingHistory.map(r => ({
      contestName: r.contestName,
      rank: r.rank,
      oldRating: r.oldRating,
      newRating: r.newRating,
      ratingChange: r.newRating - r.oldRating,
      timestamp: r.ratingUpdateTimeSeconds,
      contestId: r.contestId
    })).reverse(); // Most recent first

    const tagCount = {};
    solvedProblems.forEach(p => {
      p.tags.forEach(tag => {
        tagCount[tag] = (tagCount[tag] || 0) + 1;
      });
    });

    const tagDistData = Object.entries(tagCount)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const ratedProblems = solvedProblems.filter(p => p.rating > 0);
    const avgRating = ratedProblems.length > 0
      ? Math.round(ratedProblems.reduce((sum, p) => sum + p.rating, 0) / ratedProblems.length)
      : 0;

    return {
      userInfo,
      stats: {
        problemsSolved: solvedProblems.length,
        totalSubmissions: submissions.length,
        acceptedSubmissions: acceptedCount,
        currentStreak,
        longestStreak,
        avgRating,
        contestsParticipated: contests.length
      },
      solvedProblems,
      contests,
      charts: {
        ratingDist: ratingDistData,
        diffDist: diffDistData,
        ratingProgress: ratingProgressData,
        tagDist: tagDistData
      }
    };
  };

  const calculateStreaks = (submissions) => {
    const dates = new Set();
    submissions.forEach(sub => {
      if (sub.verdict === 'OK' && sub.creationTimeSeconds) {
        const date = new Date(sub.creationTimeSeconds * 1000).toDateString();
        dates.add(date);
      }
    });

    const sortedDates = Array.from(dates)
      .map(d => new Date(d))
      .sort((a, b) => b - a);

    let currentStreak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < sortedDates.length; i++) {
      const daysDiff = Math.floor((today - sortedDates[i]) / (1000 * 60 * 60 * 24));
      if (daysDiff === i) {
        currentStreak++;
      } else {
        break;
      }
    }

    let longestStreak = 0;
    let currentCount = 1;
    const allDates = Array.from(dates)
      .map(d => new Date(d))
      .sort((a, b) => a - b);

    for (let i = 1; i < allDates.length; i++) {
      const diff = Math.floor((allDates[i] - allDates[i - 1]) / (1000 * 60 * 60 * 24));
      if (diff === 1) {
        currentCount++;
        longestStreak = Math.max(longestStreak, currentCount);
      } else {
        currentCount = 1;
      }
    }

    return { currentStreak, longestStreak: Math.max(longestStreak, 1) };
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getSortedProblems = () => {
    if (!data) return [];
    const problems = [...data.solvedProblems];

    switch (sortBy) {
      case 'recent':
        return problems.sort((a, b) => b.timestamp - a.timestamp);
      case 'rating-high':
        return problems.sort((a, b) => b.rating - a.rating);
      case 'rating-low':
        return problems.sort((a, b) => a.rating - b.rating);
      case 'difficulty':
        return problems.sort((a, b) => a.index.localeCompare(b.index));
      default:
        return problems;
    }
  };

  const paginatedProblems = () => {
    const sorted = getSortedProblems();
    const start = currentPage * itemsPerPage;
    return sorted.slice(start, start + itemsPerPage);
  };

  const paginatedContests = () => {
    if (!data) return [];
    const start = currentPage * itemsPerPage;
    return data.contests.slice(start, start + itemsPerPage);
  };

  const totalPages = data ? Math.ceil((activeTab === 'problems' ? data.solvedProblems.length : data.contests.length) / itemsPerPage) : 0;

  const StatCard = ({ icon: Icon, label, value }) => (
    <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border p-4 sm:p-6 transition-all ${darkMode ? 'hover:border-orange-500' : 'hover:border-orange-500'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={`text-xs sm:text-sm uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1 sm:mb-2`}>{label}</p>
          <p className={`text-3xl sm:text-5xl font-light ${darkMode ? 'text-white' : 'text-black'}`}>{value}</p>
        </div>
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" strokeWidth={1.5} />
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-black' : 'bg-white'} transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Theme Toggle */}
        <div className="flex justify-end mb-4">
          <button
            onClick={toggleDarkMode}
            className={`p-3 rounded-full border ${darkMode ? 'border-gray-700 hover:border-orange-500 bg-gray-900' : 'border-gray-200 hover:border-orange-500 bg-white'} transition-all`}
            aria-label="Toggle theme"
          >
            {darkMode ? (
              <Sun className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
            ) : (
              <Moon className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
            )}
          </button>
        </div>

        {/* Header */}
        <div className={`mb-12 sm:mb-16 border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'} pb-6 sm:pb-8 text-center`}>
          <h1 className={`text-4xl sm:text-6xl font-light tracking-tight ${darkMode ? 'text-white' : 'text-black'} mb-2 sm:mb-3`}>
            Codeforces
          </h1>
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} text-sm sm:text-base tracking-wide`}>Performance Analytics Dashboard</p>
        </div>

        {/* Search */}
        <div className="mb-12 sm:mb-16">
          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchUserData()}
              placeholder="Enter handle"
              className={`flex-1 px-4 sm:px-5 py-3 sm:py-4 border ${darkMode ? 'border-gray-700 bg-gray-900 text-white placeholder-gray-500 focus:border-orange-500' : 'border-gray-300 bg-white text-black placeholder-gray-400 focus:border-orange-500'} focus:outline-none text-sm sm:text-base tracking-wide transition-colors`}
            />
            <button
              onClick={fetchUserData}
              disabled={loading}
              className={`w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-orange-500 text-white text-sm sm:text-base tracking-wide hover:bg-orange-600 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2`}
            >
              <Search className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
              {loading ? 'Loading' : 'Load'}
            </button>
          </div>
          {error && (
            <div className={`mt-4 px-4 sm:px-5 py-3 sm:py-4 border ${darkMode ? 'border-orange-500 bg-gray-900 text-white' : 'border-orange-500 bg-orange-50 text-black'} text-sm sm:text-base max-w-2xl mx-auto text-center`}>
              {error}
            </div>
          )}
        </div>

        {/* Data Display */}
        {data && (
          <>
            {/* Overall Stats */}
            <div className="mb-12 sm:mb-16">
              <h2 className={`text-xs sm:text-sm uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 sm:mb-6 text-center`}>Statistics</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-3 sm:mb-4">
                <StatCard icon={Target} label="Problems Solved" value={data.stats.problemsSolved} />
                <StatCard icon={TrendingUp} label="Total Submissions" value={data.stats.totalSubmissions} />
                <StatCard icon={Award} label="Accepted" value={data.stats.acceptedSubmissions} />
                <StatCard icon={Zap} label="Average Rating" value={data.stats.avgRating || '—'} />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatCard icon={Calendar} label="Current Streak" value={`${data.stats.currentStreak}d`} />
                <StatCard icon={Calendar} label="Longest Streak" value={`${data.stats.longestStreak}d`} />
                <StatCard icon={Trophy} label="Contests" value={data.stats.contestsParticipated} />
                <StatCard icon={Award} label="Max Rating" value={data.userInfo.maxRating || '—'} />
              </div>
            </div>

            {/* Charts */}
            <div className="mb-12 sm:mb-16">
              <h2 className={`text-xs sm:text-sm uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 sm:mb-6 text-center`}>Analysis</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {/* Rating Distribution */}
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border p-4 sm:p-6`}>
                  <h3 className={`text-xs sm:text-sm uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 sm:mb-6 text-center`}>Rating Distribution</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.charts.ratingDist}>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="rating" tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} stroke={darkMode ? '#4b5563' : '#9ca3af'} />
                      <YAxis tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} stroke={darkMode ? '#4b5563' : '#9ca3af'} />
                      <Tooltip 
                        contentStyle={{ 
                          border: darkMode ? '1px solid #fff' : '1px solid #000', 
                          backgroundColor: darkMode ? '#111' : '#fff',
                          color: darkMode ? '#fff' : '#000',
                          fontSize: '11px'
                        }} 
                      />
                      <Bar dataKey="count" fill="#f97316" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Difficulty Distribution */}
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border p-4 sm:p-6`}>
                  <h3 className={`text-xs sm:text-sm uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 sm:mb-6 text-center`}>Difficulty Distribution</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.charts.diffDist}>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="difficulty" tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} stroke={darkMode ? '#4b5563' : '#9ca3af'} />
                      <YAxis tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} stroke={darkMode ? '#4b5563' : '#9ca3af'} />
                      <Tooltip 
                        contentStyle={{ 
                          border: darkMode ? '1px solid #fff' : '1px solid #000', 
                          backgroundColor: darkMode ? '#111' : '#fff',
                          color: darkMode ? '#fff' : '#000',
                          fontSize: '11px'
                        }} 
                      />
                      <Bar dataKey="count">
                        {data.charts.diffDist.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#f97316' : '#fb923c'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Rating Progress */}
                {data.charts.ratingProgress.length > 0 && (
                  <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border p-4 sm:p-6`}>
                    <h3 className={`text-xs sm:text-sm uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 sm:mb-6 text-center`}>Rating Progress</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={data.charts.ratingProgress}>
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} stroke={darkMode ? '#4b5563' : '#9ca3af'} />
                        <YAxis tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} stroke={darkMode ? '#4b5563' : '#9ca3af'} />
                        <Tooltip 
                          contentStyle={{ 
                            border: darkMode ? '1px solid #fff' : '1px solid #000', 
                            backgroundColor: darkMode ? '#111' : '#fff',
                            color: darkMode ? '#fff' : '#000',
                            fontSize: '11px'
                          }} 
                        />
                        <Line type="monotone" dataKey="rating" stroke="#f97316" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Tag Distribution */}
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border p-4 sm:p-6`}>
                  <h3 className={`text-xs sm:text-sm uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 sm:mb-6 text-center`}>Top Tags</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.charts.tagDist} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} stroke={darkMode ? '#4b5563' : '#9ca3af'} />
                      <YAxis dataKey="tag" type="category" width={80} tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} stroke={darkMode ? '#4b5563' : '#9ca3af'} />
                      <Tooltip 
                        contentStyle={{ 
                          border: darkMode ? '1px solid #fff' : '1px solid #000', 
                          backgroundColor: darkMode ? '#111' : '#fff',
                          color: darkMode ? '#fff' : '#000',
                          fontSize: '11px'
                        }} 
                      />
                      <Bar dataKey="count" fill="#f97316" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className={`mb-8 sm:mb-12 flex justify-center border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <button
                onClick={() => { setActiveTab('problems'); setCurrentPage(0); }}
                className={`px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm tracking-wide transition-colors ${
                  activeTab === 'problems' 
                    ? 'border-b-2 border-orange-500 text-orange-500' 
                    : `${darkMode ? 'text-gray-400 hover:text-orange-500' : 'text-gray-500 hover:text-orange-500'}`
                }`}
              >
                PROBLEMS
              </button>
              <button
                onClick={() => { setActiveTab('contests'); setCurrentPage(0); }}
                className={`px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm tracking-wide transition-colors ${
                  activeTab === 'contests' 
                    ? 'border-b-2 border-orange-500 text-orange-500' 
                    : `${darkMode ? 'text-gray-400 hover:text-orange-500' : 'text-gray-500 hover:text-orange-500'}`
                }`}
              >
                CONTESTS
              </button>
            </div>

            {/* Problems List */}
            {activeTab === 'problems' && (
              <div className="mb-12 sm:mb-16">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <h2 className={`text-xs sm:text-sm uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Solved Problems</h2>
                  <select
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setCurrentPage(0); }}
                    className={`w-full sm:w-auto px-4 sm:px-5 py-2 sm:py-3 border ${darkMode ? 'border-gray-700 bg-gray-900 text-white focus:border-orange-500' : 'border-gray-300 bg-white text-black focus:border-orange-500'} focus:outline-none text-xs sm:text-sm tracking-wide`}
                  >
                    <option value="recent">Recent</option>
                    <option value="rating-high">Rating ↓</option>
                    <option value="rating-low">Rating ↑</option>
                    <option value="difficulty">Difficulty</option>
                  </select>
                </div>

                <div className="space-y-3">
                  {paginatedProblems().map((problem, idx) => {
                    const dateTime = formatDateTime(problem.timestamp);
                    return (
                      <div key={idx} className={`border ${darkMode ? 'border-gray-700 hover:border-orange-500' : 'border-gray-200 hover:border-orange-500'} p-4 sm:p-5 transition-colors`}>
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                          <a
                            href={`https://codeforces.com/problemset/problem/${problem.contestId}/${problem.index}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${darkMode ? 'text-white hover:text-orange-500' : 'text-black hover:text-orange-500'} text-sm sm:text-base flex-1 transition-colors`}
                          >
                            {problem.name}
                          </a>
                          <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0">
                            <div className={`flex items-center gap-2 text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="font-mono">{dateTime.date}</span>
                              <span className="font-mono hidden sm:inline">{dateTime.time}</span>
                            </div>
                            <span className={`text-xs sm:text-sm font-mono ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {problem.index}
                            </span>
                            <span className="text-xs sm:text-sm font-mono text-orange-500 font-bold">
                              {problem.rating || '—'}
                            </span>
                          </div>
                        </div>
                        {problem.tags.length > 0 && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            {problem.tags.slice(0, 5).map((tag, i) => (
                              <span key={i} className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} tracking-wide`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contests List */}
            {activeTab === 'contests' && (
              <div className="mb-12 sm:mb-16">
                <h2 className={`text-xs sm:text-sm uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-6`}>Contest History</h2>
                <div className="space-y-3">
                  {paginatedContests().map((contest, idx) => {
                    const dateTime = formatDateTime(contest.timestamp);
                    const isPositive = contest.ratingChange >= 0;
                    return (
                      <div key={idx} className={`border ${darkMode ? 'border-gray-700 hover:border-gray-500' : 'border-gray-200 hover:border-black'} p-4 sm:p-5 transition-colors`}>
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                          <a
                            href={`https://codeforces.com/contest/${contest.contestId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${darkMode ? 'text-white hover:text-gray-400' : 'text-black hover:text-gray-600'} text-sm sm:text-base flex-1 transition-colors`}
                          >
                            {contest.contestName}
                          </a>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                            <div className={`flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="font-mono">{dateTime.date}</span>
                            </div>
                            <span className={`font-mono ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rank: {contest.rank}</span>
                            <span className={`font-mono font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                              {isPositive ? '+' : ''}{contest.ratingChange}
                            </span>
                            <span className={`font-mono ${darkMode ? 'text-white' : 'text-black'}`}>{contest.newRating}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pagination */}
            <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 sm:pt-8 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2 sm:py-3 border ${darkMode ? 'border-gray-700 text-white hover:border-gray-500' : 'border-gray-300 text-black hover:border-black'} text-xs sm:text-sm tracking-wide ${darkMode ? 'disabled:text-gray-600 disabled:border-gray-800' : 'disabled:text-gray-300 disabled:border-gray-200'} transition-colors flex items-center justify-center gap-2`}
              >
                <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" strokeWidth={1.5} />
                Previous
              </button>
              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} tracking-wide font-mono`}>
                {currentPage + 1} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2 sm:py-3 border ${darkMode ? 'border-gray-700 text-white hover:border-gray-500' : 'border-gray-300 text-black hover:border-black'} text-xs sm:text-sm tracking-wide ${darkMode ? 'disabled:text-gray-600 disabled:border-gray-800' : 'disabled:text-gray-300 disabled:border-gray-200'} transition-colors flex items-center justify-center gap-2`}
              >
                Next
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" strokeWidth={1.5} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CodeforcesDashboard;